/**
 * Прогрев PMTiles и единый экземпляр протокола для GL-карт.
 *
 * ЗАЧЕМ. Первый кадр карты на медленном канале ждёт цепочку последовательных
 * Range-запросов PMTiles: заголовок+корневая директория (первые 16 КБ файла)
 * → листовые директории → сами тайлы. Каждый запрос — отдельный round-trip;
 * на слабой сети именно они, а не объём данных, определяют задержку до первого
 * кадра. Прогрев заранее выполняет самый первый запрос (getHeader читает
 * заголовок И корневую директорию одним чтением) и кладёт результат в кэш
 * pmtiles.js. К моменту создания карты этот шаг уже сделан (между сессиями
 * диапазоны дополнительно переживают благодаря Service Worker'у sw.js).
 *
 * КАК ЭТО СВЯЗЫВАЕТСЯ С КАРТОЙ. pmtiles.js обслуживает запрос тайла так: парсит
 * URL `pmtiles://<file>/<z>/<x>/<y>`, берёт `<file>` как ключ и ищет PMTiles
 * instance в своей внутренней Map. Если instance уже добавлен (protocol.add),
 * переиспользует его вместе с прогретым кэшем; иначе создаёт новый с пустым
 * кэшем. Поэтому прогрев обязан:
 *   1) использовать ТОТ ЖЕ Protocol, что отдаётся картам (отсюда — единый
 *      модульный экземпляр, а не `new Protocol()` в каждом компоненте);
 *   2) создавать PMTiles с URL БЕЗ префикса `pmtiles://` — именно такой ключ
 *      (getKey → FetchSource.url) сойдётся с тем, что парсер выделяет из URL
 *      тайла. С префиксом ключи не совпадут и прогретый instance не подхватится.
 *
 * ВАЖНО ПРО БАНДЛ: этот модуль импортируется из App.tsx (warmPmtiles), поэтому
 * он НЕ должен статически импортировать maplibre-gl — иначе вся библиотека
 * (~250 КБ gzip) въезжает в main-чанк и «ленивая» загрузка карт (lazy(() =>
 * import('./HistoryMapGl'))) становится фикцией. Отсюда два решения:
 *   • getPmtilesProtocol ПРИНИМАЕТ модуль maplibre от вызывающего (компоненты
 *     карт и так его импортируют — они внутри lazy-чанков);
 *   • warmPmtiles подтягивает maplibre ДИНАМИЧЕСКИМ import'ом — это заодно
 *     скачивает чанк библиотеки заранее (префетч), и prewarm() поднимает пул
 *     web worker'ов до конструктора Map (создание воркеров — заметные
 *     десятки мс на слабых устройствах, и они лежат на критическом пути).
 * Статический импорт из maplibre-gl здесь разрешён ТОЛЬКО типовой
 * (import type — стирается при компиляции). pmtiles маленький, он не в счёт.
 *
 * Прогрев идемпотентен и не блокирует: getHeader кэшируется (SharedPromiseCache),
 * повторные вызовы возвращают тот же промис; ошибка сети проглатывается —
 * карта в худшем случае просто сделает запрос сама, как и раньше.
 */
import type maplibregl from 'maplibre-gl';
import { Protocol, PMTiles } from 'pmtiles';
import { TILES_URL } from './mapStyle';

// URL файла без схемы `pmtiles://` — в этом виде pmtiles.js хранит ключ instance
// и в этом же виде его выделяет из URL тайла. Должны совпадать байт-в-байт.
const PMTILES_FILE_URL = TILES_URL.replace(/^pmtiles:\/\//, '');

// Единый протокол на всё приложение. Обе GL-карты берут именно его (см.
// getPmtilesProtocol) — тогда прогретый кэш общий для основной карты и истории.
const protocol = new Protocol();

// Единый PMTiles instance, добавленный в протокол под правильным ключом. Его
// кэш и греется, и переиспользуется картами.
const archive = new PMTiles(PMTILES_FILE_URL);
protocol.add(archive);

let warmPromise: Promise<unknown> | null = null;
let protocolRegistered = false;
let maplibreWarmed = false;
let assetsWarmed = false;

/**
 * Возвращает общий протокол и одноразово регистрирует его в maplibre-gl.
 * Модуль maplibre передаёт ВЫЗЫВАЮЩИЙ (компонент карты — он внутри lazy-чанка
 * и уже импортировал библиотеку); статический импорт здесь запрещён, см. шапку.
 * В cleanup протокол НЕ снимать (removeProtocol) — он общий и живёт всю
 * сессию; снятие сломало бы вторую карту.
 */
export const getPmtilesProtocol = (ml: typeof maplibregl): Protocol => {
    if (!protocolRegistered) {
        ml.addProtocol('pmtiles', protocol.tile);
        protocolRegistered = true;
    }
    return protocol;
};

/** Статика стиля: спрайт по DPR + первые глиф-диапазоны (лат + кириллица). */
const warmStyleAssets = (): void => {
    if (assetsWarmed || typeof window === 'undefined') return;
    assetsWarmed = true;
    const origin = window.location.origin;
    const suffix = window.devicePixelRatio > 1 ? '@2x' : '';
    const urls = [
        `${origin}/map-sprite/sprite${suffix}.json`,
        `${origin}/map-sprite/sprite${suffix}.png`,
        // Диапазоны глифов: 0-255 (латиница/цифры), 1024-1279 (кириллица) —
        // покрывают подписи дорог/посёлков первого кадра. Стеки — из mapStyle.
        `${origin}/map-glyphs/IBM Plex Sans Regular/0-255.pbf`,
        `${origin}/map-glyphs/IBM Plex Sans Regular/1024-1279.pbf`,
        `${origin}/map-glyphs/IBM Plex Sans Medium/0-255.pbf`,
        `${origin}/map-glyphs/IBM Plex Sans Medium/1024-1279.pbf`,
    ];
    for (const u of urls) {
        // Обычный GET: ответ ложится в HTTP-кэш браузера (map-sprite/map-glyphs
        // отдаются с Cache-Control, см. ServeStaticModule), откуда его возьмёт
        // карта. Ошибки не важны — карта запросит сама.
        fetch(u).catch(() => undefined);
    }
};

/**
 * Прогревает всё, что лежит на критическом пути первого кадра карты:
 * чанк maplibre-gl + пул его воркеров, заголовок+корневую директорию архива,
 * спрайт и глифы. Безопасно звать многократно: работа выполняется один раз.
 * Вызывать при входе в приложение / заходе на маршрут карты, ДО монтирования
 * компонента карты.
 */
export const warmPmtiles = (): Promise<unknown> => {
    if (!maplibreWarmed) {
        maplibreWarmed = true;
        // Динамический импорт = префетч lazy-чанка библиотеки + prewarm пула
        // воркеров. При ошибке (обрыв сети) сбрасываем флаг — следующий вызов
        // попробует снова; карта тем временем загрузит чанк штатно сама.
        import('maplibre-gl')
            .then((mod) => mod.default.prewarm())
            .catch(() => {
                maplibreWarmed = false;
            });
    }
    warmStyleAssets();
    if (!warmPromise) {
        warmPromise = archive.getHeader().catch(() => {
            // Прогрев — оптимизация, не обязательство. Если сеть подвела, сбросим
            // промис, чтобы следующий заход попробовал снова; карта тем временем
            // отработает штатно (сделает запрос сама).
            warmPromise = null;
        });
    }
    return warmPromise;
};